import { BillDataEntity, GstTreatment } from "../../domain/entities/BillEntity";
import type { Currency } from "../../../../services/api";
import { zournalPartyId } from "../../../../shared/utils/buildManualDocumentJson";
import {
  resolveCurrencyFieldsFromCatalog,
} from "../../../../shared/utils/manualDocumentCurrency";
import {
  getCountryAbbreviation,
  getStateAbbreviation,
} from "../../../../utils";

/** `location.state.returnTo` when opening Add/Edit party from create-manual bill flow */
export const CREATE_MANUAL_BILL_RETURN_TO = "create-manual-bill";

export const CREATE_MANUAL_BILL_ROUTE = "/bills/manual/new";

export type CreateManualBillNavigationState = {
  returnTo?: string;
  createManualReturnPath?: string;
  billDraft?: BillDataEntity;
  applyVendorPatch?: Partial<
    Pick<
      BillDataEntity,
      | "vendor_id"
      | "vendor_name"
      | "vendor_gstin"
      | "vendor_address"
      | "source_of_supply"
      | "destination_of_supply"
      | "place_of_supply"
      | "gst_no"
      | "gst_treatment"
      | "gst_category"
    >
  >;
};

export type VendorLikeForManualBill = {
  gst_treatment?: GstTreatment;
  /** "rcm" marks a domestic reverse-charge vendor; overseas implies it too. */
  gst_category?: string;
  zoho_vendorid?: string;
  vendorid?: string;
  vendor_id?: string | number;
  vendor_legal_name?: string;
  name?: string;
  gstin?: string;
  state?: string | null;
  country?: string | null;
  registered_office_address?: string;
  address?: string;
  /** From API search — code/symbol resolved via currencies catalog */
  currency_id?: string | number;
};

/**
 * Vendor-only fields for manual bill JSON (vendor supply + company destination).
 * Matches legacy CreateManualBillPage handleVendorSelect mapping.
 */
export function buildManualBillVendorPatch(
  vendor: VendorLikeForManualBill,
  companyDestinationStateAbbrev: string,
  currencies: Currency[] = [],
): Pick<
  BillDataEntity,
  | "vendor_id"
  | "vendor_name"
  | "vendor_gstin"
  | "vendor_address"
  | "source_of_supply"
  | "destination_of_supply"
  | "place_of_supply"
  | "gst_no"
  | "gst_treatment"
  | "gst_category"
  | "currency_id"
  | "currency_code"
  | "currency_symbol"
> {
  const vendorId = zournalPartyId(vendor);
  const vendorName = (vendor.vendor_legal_name || vendor.name || "").trim();
  const gstin = (vendor.gstin || "").trim();
  const address = (
    vendor.registered_office_address ||
    vendor.address ||
    ""
  ).trim();
  const vendorStateRaw = vendor.state ?? "";
  const vendorStateAbbrev =
    getStateAbbreviation(vendorStateRaw) ||
    getCountryAbbreviation(vendor.country) ||
    "";

  const currencyId = String(vendor.currency_id ?? "").trim();
  const currencyResolved = currencyId
    ? resolveCurrencyFieldsFromCatalog(currencies, currencyId)
    : null;

  return {
    vendor_id: vendorId,
    vendor_name: vendorName,
    vendor_gstin: gstin,
    gst_no: gstin,
    gst_treatment: vendor.gst_treatment || "",
    gst_category: (vendor.gst_category || "").trim(),
    vendor_address: address,
    source_of_supply: vendorStateAbbrev,
    destination_of_supply: companyDestinationStateAbbrev,
    place_of_supply: vendorStateAbbrev,
    currency_id: currencyResolved?.currency_id ?? currencyId,
    currency_code: currencyResolved?.currency_code ?? "",
    currency_symbol: currencyResolved?.currency_symbol ?? "",
  };
}

export function isCreateManualBillReturnState(
  state: unknown,
): state is CreateManualBillNavigationState & {
  createManualReturnPath: string;
  billDraft: BillDataEntity;
} {
  if (!state || typeof state !== "object") return false;
  const s = state as CreateManualBillNavigationState;
  return (
    s.returnTo === CREATE_MANUAL_BILL_RETURN_TO &&
    typeof s.createManualReturnPath === "string" &&
    s.createManualReturnPath.length > 0 &&
    s.billDraft != null &&
    typeof s.billDraft === "object"
  );
}
